import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Button } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';

const FileModal = ({ 
  isOpen, 
  toggle, 
  onImport, 
  onExport,
  importLabel = "Import from Excel",
  exportLabel = "Export to Excel",
  importHelpText = "Supports .xlsx, .xls, .csv formats",
  exportHelpText = "Exports all data in Excel format",
  importAccept = ".xlsx, .xls, .csv",
  importNeed = true,
  error: propError, // Error passed from parent component
  loading // Loading state from parent
}) => {
  const [importFile, setImportFile] = useState(null);
  const [localError, setLocalError] = useState('');

  // Clear errors when modal is opened/closed
  useEffect(() => {
    if (!isOpen) {
      setImportFile(null);
      setLocalError('');
    }
  }, [isOpen]);

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLocalError('');
    setImportFile(file);

    if (onImport) {
      try {
        await onImport(file);
        // Clear file input after successful import
        e.target.value = '';
        setImportFile(null);
      } catch (err) {
        setLocalError(err.message || 'Failed to process import file');
        console.error('Import error:', err);
      }
    }
  };

  const handleExport = async () => {
    if (onExport) {
      try {
        await onExport();
      } catch (err) {
        setLocalError(err.message || 'Failed to export data');
        console.error('Export error:', err);
      }
    }
  };

  // Combine parent error and local error
  const errorToDisplay = propError || localError;

  return (
    <Modal 
      isOpen={isOpen} 
      toggle={toggle} 
      size="lg"
      className="fade-scale-modal"
      backdropClassName="modal-backdrop-blur"
      centered
    >
      <div className="modal-header text-white">
        <h5 className="modal-title fs-4">Import/Export Data</h5>
        <button 
          type="button" 
          className="btn-close btn-close-white" 
          onClick={toggle}
          aria-label="Close"
        ></button>
      </div>
      
      <div className="modal-body p-4">
        {/* Import Section */}
        {importNeed && (
          <Row className="mb-4">
            <Col md={12}>
              <div className="form-group">
                <label htmlFor="fileImport" className="form-label fw-medium d-flex align-items-center">
                  <FeatherIcon icon="upload" className="me-2" />
                  {importLabel}
                </label>
                <div className="d-flex align-items-center gap-3">
                  <input
                    type="file"
                    id="fileImport"
                    className="form-control"
                    accept={importAccept}
                    onChange={handleImport}
                    disabled={loading}
                  />
                  {importFile && (
                    <span className="text-truncate" style={{ maxWidth: '200px' }}>
                      {importFile.name}
                    </span>
                  )}
                </div>
                <small className="text-muted">{importHelpText}</small>
              </div>
            </Col>
          </Row>
        )}

        {/* Export Section */}
        <Row>
          <Col md={12}>
            <div className="form-group">
              <label htmlFor="fileExport" className="form-label fw-medium d-flex align-items-center">
                <FeatherIcon icon="download" className="me-2" />
                {exportLabel}
              </label>
              <Button 
                color="primary" 
                onClick={handleExport}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Download Excel File'}
              </Button>
              <small className="text-muted d-block mt-1">
                {exportHelpText}
              </small>
            </div>
          </Col>
        </Row>

        {errorToDisplay && (
          <Row className="mt-3">
            <Col md={12}>
              <div className="alert alert-danger">
                {errorToDisplay}
              </div>
            </Col>
          </Row>
        )}
      </div>
      
      <div className="modal-footer bg-light border-top-0">
        <button 
          type="button" 
          className="btn btn-outline-secondary rounded-pill px-4 me-2" 
          onClick={toggle}
          disabled={loading}
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

export default FileModal;